import { CompactInlineAlertWidget, InlineAlertType, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { HelpService } from '~src/commonInfra/services/HelpService';

import { Messages } from '../../../codegen/Localize';
import { ReactActionDialogBodyProps } from '../../../commonInfra/dialogs/ReactActionDialog';
import { ISiteUser } from '../../../userManagement/users/serverApi/types/ISiteUser';
import { AuthenticationProviderSelector } from '../directives/AuthenticationProviderSelector';
import { AuthenticationProviderSelectorProperties } from '../model/AuthenticationProviderSelectorProperties';
import { AuthenticationProviderType, IAuthenticationProvider } from '../services/SiteAuthenticationSettingsService';


export interface UpdateUserAuthenticationDialogProps {
    currentUser: ISiteUser;
    isAboutToBeLoggedOut: boolean;
    isAdminToBeUpdated: boolean;
    usernameToUpdate?: string;
}

export interface UpdateUserAuthenticationDialogState extends AuthenticationProviderSelectorProperties {
    oldAuthenticationProvider: IAuthenticationProvider;
}

export type UpdateUserAuthenticationActionDialogBodyProps = ReactActionDialogBodyProps<UpdateUserAuthenticationDialogProps, UpdateUserAuthenticationDialogState>;

export function UpdateUserAuthenticationDialogBody(props: UpdateUserAuthenticationActionDialogBodyProps) {
    const [isAboutToBeLoggedOut, setAboutToBeLoggedOut] = React.useState(props.isAboutToBeLoggedOut);
    const [isAdminToBeUpdated] = React.useState(props.isAdminToBeUpdated);

    const onProviderChange = (provider: IAuthenticationProvider) => {props.updateDialogState({authenticationProvider: provider});};
    const onProviderIdsChange = (ids: string[]) => {props.updateDialogState({authenticationProviderIds: ids});};
    const onAuthSettingChange = (authSetting: string) => {props.updateDialogState({authSetting: authSetting});};

    const isAuthUserIdGoingToChange = (oldProvider: IAuthenticationProvider, newProvider: IAuthenticationProvider) => {
        // This assumes that switching between SAML & OIDC won't result in the user being logged out,
        // and switching between any pair of other auth types will. The logout happens when Vizportal
        // re-associates the site user to a different system user (the system user's identity is determined by
        // authUserId).
        const equivalenceClass = (provider?: IAuthenticationProvider) => {
            if (!provider) {
                return -1;
            }
            switch(provider.type) {
                case AuthenticationProviderType.default:
                    return 0;
                case AuthenticationProviderType.oidc:
                case AuthenticationProviderType.saml:
                    return 1;
                case AuthenticationProviderType.tabidmfa:
                    return 2;
                default:
                    return -2;
            }
        };
        return equivalenceClass(oldProvider) !== equivalenceClass(newProvider);
    };

    React.useEffect(
      () => {
        setAboutToBeLoggedOut(isAuthUserIdGoingToChange(
            props.dialogState.oldAuthenticationProvider,
            props.dialogState.authenticationProvider));
      },
      // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [props.dialogState.authenticationProvider]
    );

    return (
        <div className="tb-user-authentication-options">
            <AuthenticationProviderSelector {...props.dialogState} onProviderChange={onProviderChange} onProviderIdsChange={onProviderIdsChange} onAuthSettingChange={onAuthSettingChange} displayUsername={props?.usernameToUpdate}/>
            {isAboutToBeLoggedOut ?
                    <CompactInlineAlertWidget type={InlineAlertType.Warning} testId='' style={{ paddingTop: '8px' }}>
                        {isAdminToBeUpdated ?
                            <div style={{ paddingBottom: '16px' }}>
                                <div style={{ marginBottom: '24px' }} data-tb-test-id={'changing-user-sso-text'}>
                                    {Messages.ssoBeforeYouContinue_aboutToBeLoggedOut_changingAdmin()}
                                </div>
                                <div>
                                    {Messages.ssoBeforeYouContinue_aboutToBeLoggedOut_adminRecommendation()}
                                    <TextLinkWidget
                                        className=".tb-ssoBeforeYouContinue-learnMore"
                                        testId={'changing-admin-mfa-learn-more'}
                                        href={HelpService.buildUrl('mfa-auth-change-warning')}
                                        style={{ fontWeight: 500, paddingLeft: '2px'}}>
                                        {Messages.learnMore()}
                                    </TextLinkWidget>
                                </div>
                            </div>
                        :
                            <div data-tb-test-id={'changing-user-sso-text'} style={{ paddingBottom: '12px'}}>
                                {Messages.ssoBeforeYouContinue_aboutToBeLoggedOut()}
                            </div>}
                    </CompactInlineAlertWidget>
            : null}
        </div>
    );
}

export function UpdateUserAuthenticationWarningDialog(): JSX.Element {
    return (
        <div>
            {Messages.updateUserAuthenticationWarning()}
            <div className="tb-dialog-error">{Messages.updateUserAuthenticationWarningDetails()}</div>
        </div>
    );
}
